import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Modal from "react-modal";
import { Times } from "@styled-icons/fa-solid/Times";
import { InfoCircle } from "@styled-icons/fa-solid/InfoCircle";
import { device } from "../../constants/device-sizes";

const Banner = styled.div`
  position: absolute;
  top: 0.85rem;
  left: 0;
  right: 0;
  /* No background — the buttons (DM/refresh on the left, status/gear on the
     right) sit on this same row, so keep the centered content in a middle
     column with side padding to clear them and avoid overlap. */
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 9rem;
  box-sizing: border-box;
  color: var(--text-color, #ccc);
  font-size: 0.8rem;
  text-align: center;
  pointer-events: none;
  z-index: 198;

  /* re-enable clicks only on interactive children (the sign-in button) */
  & > * {
    pointer-events: auto;
  }
`;

const Countdown = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
`;

const InfoButton = styled(InfoCircle)`
  width: 1rem;
  height: 1rem;
  margin-left: 6px;
  cursor: pointer;
  color: var(--text-color, #ccc);
  opacity: 0.8;
  flex-shrink: 0;
  &:hover {
    opacity: 1;
  }
`;

const ModalContent = styled.div`
  background-color: var(--clr-bg);
  border-radius: 14px;
  padding: 1.75rem 1.5rem 1.5rem;
  width: 90%;
  max-width: 420px;
  position: relative;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
  @media ${device.tablet} {
    width: 420px;
  }
`;

const CloseButton = styled(Times)`
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  width: 1.6rem;
  height: 1.6rem;
  cursor: pointer;
  color: var(--clr-sec);
  transition: color 0.15s linear;
  &:hover {
    color: red;
  }
`;

const ModalTitle = styled.h2`
  color: var(--clr-sec);
  font-size: 1.25rem;
  margin: 0 0 0.75rem;
`;

const ModalText = styled.p`
  color: var(--clr-sec);
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0 0 1.25rem;
  opacity: 0.9;
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.7rem 1rem;
  border: none;
  border-radius: 24px;
  background: var(--clr-purple);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s linear;
  &:hover {
    filter: brightness(1.1);
  }
`;

const Emphasis = styled.strong`
  color: var(--clr-sec);
  font-weight: 700;
`;

const Subnote = styled.div`
  color: var(--clr-sec);
  font-size: 0.78rem;
  opacity: 0.65;
  text-align: center;
  margin-top: 0.7rem;
`;

/**
 * Small banner above the input shown only while progressive-trust
 * restrictions are active (driven by the server's `trust_status` event).
 * During the read-only window the user can still compose — only sending is
 * held — so the copy frames it as a short wait, not a block.
 */
function TrustBanner({
  readOnlyUntil,
  slowModeSeconds,
  restrictions = [],
  onSignInWithGoogle,
}) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    if (!readOnlyUntil || readOnlyUntil <= Date.now()) {
      setSecondsLeft(0);
      return undefined;
    }
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((readOnlyUntil - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [readOnlyUntil]);

  if (secondsLeft > 0) {
    return (
      <Banner>
        Welcome! You can read and type now — sending unlocks in{" "}
        <Countdown>{secondsLeft}s</Countdown>
      </Banner>
    );
  }

  if (restrictions.includes("slow_mode") && slowModeSeconds) {
    return (
      <>
        <Banner>
          Slow mode enabled for new accounts to stop trolls.
          <InfoButton
            role="button"
            tabIndex={0}
            aria-label="Why am I in slow mode?"
            onClick={() => setInfoOpen(true)}
          />
        </Banner>
        <Modal
          isOpen={infoOpen}
          onRequestClose={() => setInfoOpen(false)}
          ariaHideApp={false}
          style={{
            overlay: { zIndex: 1000, backgroundColor: "rgba(0,0,0,0.5)" },
            content: {
              background: "none",
              border: "none",
              padding: 0,
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          }}
        >
          <ModalContent>
            <CloseButton onClick={() => setInfoOpen(false)} />
            <ModalTitle>Why slow mode?</ModalTitle>
            <ModalText>
              To keep trolls and ban-evaders from spamming, new accounts
              can only send one message every {slowModeSeconds} seconds. This lifts
              automatically as you keep chatting. You can also sign in with
              Google to remove slow mode. Your Google account is <Emphasis>private</Emphasis> and not
              shown to anyone, and you can remain <Emphasis>anonymous</Emphasis> after signing in.
            </ModalText>
            {onSignInWithGoogle && (
              <>
                <GoogleButton
                  type="button"
                  onClick={() => {
                    setInfoOpen(false);
                    onSignInWithGoogle();
                  }}
                >
                  Sign in with Google to chat freely
                </GoogleButton>
              </>
            )}
          </ModalContent>
        </Modal>
      </>
    );
  }

  return null;
}

export default TrustBanner;
